import React, { useState, useEffect, useCallback } from "react";
import { Bell, WifiOff, CheckCircle, AlertTriangle, XCircle, ArrowUpCircle, Download, Cpu, Activity } from "lucide-react";

const NotificationCenter = ({ api }) => {
  const [alerts, setAlerts] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = React.useRef(null);

  const fetchAlerts = useCallback(async () => {
    try {
      const [alertsRes, countRes] = await Promise.all([
        api.get("/alerts?limit=20"),
        api.get("/alerts/active-count")
      ]);
      setAlerts(alertsRes.data.alerts || []);
      setUnreadCount(countRes.data.active_alerts || 0);
    } catch {}
  }, [api]);

  useEffect(() => {
    fetchAlerts();
    const interval = setInterval(fetchAlerts, 15000);
    return () => clearInterval(interval);
  }, [fetchAlerts]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const resolveAlert = async (alertId) => {
    try {
      await api.put(`/alerts/${alertId}/resolve`);
      fetchAlerts();
    } catch {}
  };

  const resolveAll = async () => {
    try {
      await api.post('/alerts/resolve-all');
      fetchAlerts();
    } catch {}
  };

  const getAlertIcon = (type) => {
    switch (type) {
      case 'offline': return <WifiOff className="w-4 h-4 text-red-400 flex-shrink-0" />;
      case 'recovered': return <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />;
      case 'encoder_fallback': return <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0" />;
      case 'crash_limit': return <XCircle className="w-4 h-4 text-red-400 flex-shrink-0" />;
      case 'update_success': return <ArrowUpCircle className="w-4 h-4 text-green-400 flex-shrink-0" />;
      case 'ffmpeg_update': return <Download className="w-4 h-4 text-blue-400 flex-shrink-0" />;
      case 'high_cpu': return <Cpu className="w-4 h-4 text-amber-400 flex-shrink-0" />;
      default: return <Activity className="w-4 h-4 text-zinc-400 flex-shrink-0" />;
    }
  };

  const formatTime = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    const now = new Date();
    const diffMs = now - d;
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHrs = Math.floor(diffMins / 60);
    if (diffHrs < 24) return `${diffHrs}h ago`;
    const diffDays = Math.floor(diffHrs / 24);
    return `${diffDays}d ago`;
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        data-testid="notification-bell"
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-zinc-400 hover:text-white transition-colors"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold" data-testid="notification-count">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-96 bg-zinc-900 border border-zinc-700 rounded-lg shadow-2xl z-50 max-h-[500px] flex flex-col" data-testid="notification-panel">
          <div className="flex items-center justify-between p-3 border-b border-zinc-800">
            <h3 className="text-white text-sm font-medium">Notifications</h3>
            <div className="flex gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={resolveAll}
                  className="text-xs text-amber-400 hover:text-amber-300"
                >
                  Resolve All
                </button>
              )}
            </div>
          </div>

          <div className="overflow-y-auto flex-1">
            {alerts.length === 0 ? (
              <div className="p-6 text-center text-zinc-500 text-sm">
                No notifications
              </div>
            ) : (
              alerts.map((alert) => (
                <div
                  key={alert.id}
                  className={`flex items-start gap-3 p-3 border-b border-zinc-800/50 hover:bg-zinc-800/30 transition-colors ${
                    !alert.resolved ? 'bg-zinc-800/20' : ''
                  }`}
                >
                  {getAlertIcon(alert.alert_type)}
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm truncate">
                      <span className="font-medium">{alert.site_name}</span>
                      {' — '}
                      {alert.message || alert.alert_type?.replace(/_/g, ' ')}
                    </p>
                    <p className="text-zinc-500 text-xs mt-0.5">{alert.agent_event_schema === 1 ? <>Occurred {formatTime(alert.occurred_at)} · received {formatTime(alert.received_at)}</> : formatTime(alert.timestamp)}</p>
                    {alert.agent_event_schema === 1 && <p className="text-zinc-500 text-xs mt-0.5">External notification: {{delivered:'delivered',pending:'waiting to send',sending:'sending',failed:'delivery failed',disabled:'disabled',not_eligible:'not requested by policy'}[alert.notification_status] || 'unverified'}</p>}
                  </div>
                  {!alert.resolved && (
                    <button
                      onClick={(e) => { e.stopPropagation(); resolveAlert(alert.id); }}
                      className="text-zinc-500 hover:text-green-400 flex-shrink-0 p-1"
                      title="Resolve"
                    >
                      <CheckCircle className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationCenter;
